import type {
  BatchResultStatus,
  ContractFunctionResult,
} from "@eulerxyz/evc-sdk"
import type { Address } from "viem"
import { GenericVaultType } from "../../hooks"
import { WETHTransaction, WETHTransactionItem } from "./WETHTransactionTypes"
import {
  CompoundTransactionItem,
  CompoundTransactionUnion,
} from "./compoundTransactionTypes"
import {
  EulerSwapTransaction,
  EulerSwapTransactionItem,
  UninstallTransaction,
  UninstallTransactionItem,
} from "./eulerSwapTypes"
import type { EvcTransaction } from "./evcTransactionTypes"
import {
  FeeFlowTransaction,
  FeeFlowTransactionItem,
} from "./feeFlowTransactionTypes"
import {
  KeyringTransaction,
  KeyringTransactionItem,
} from "./keyringTransactionTypes"
import type {
  PermitTransactionBase,
  PermitTransactionItem,
} from "./permit2TransactionTypes"
import type {
  RewardStreamsTransaction,
  RewardStreamsTransactionItem,
} from "./rewardTransactionType"
import {
  MerklTransaction,
  MerklTransactionItem,
  UnlockREulTransaction,
  UnlockREulTransactionItem,
} from "./rewardTransactionType"
import type {
  SwapTransaction,
  SwapTransactionItem,
} from "./swapTransactionTypes"
import type { SwapVerifierTransaction } from "./swapVerifierTransactionTypes"
import { TosTransaction, TosTransactionItem } from "./tosTransactionTypes"
import type {
  SkimTransactionItem,
  SkimInTransactionItem,
  VaultTransaction,
  VaultTransactionItem,
} from "./vaultTransactionTypes"

export enum TransactionLabel {
  // vault specific
  BORROW = "borrow",
  DEPOSIT = "deposit",
  REPAY = "repay",
  REPAYWITHSHARES = "repayWithShares",
  TRANSFER = "transfer",
  WITHDRAW = "withdraw",
  SKIM = "skim",
  SKIM_IN = "skimIn",
  REDEEM = "redeem",
  ENABLE_BALANCE_FORWARDER = "enableBalanceForwarder",
  DISABLE_BALANCE_FORWARDER = "disableBalanceForwarder",
  CONVERT_FEES = "convertFees",
  // compound transactions
  LEVERAGE = "leverage",
  CREATE_DEBT = "createDebt",
  VAULT_SWAP = "vaultSwap",
  VAULT_SWAP_DEBT = "vaultSwapDebt",
  VAULT_SWAP_REPAY = "vaultSwapRepay",
  // evc specific
  ENABLE_COLLATERAL = "enableCollateral",
  ENABLE_CONTROLLER = "enableController",
  DISABLE_COLLATERAL = "disableCollateral",
  DISABLE_CONTROLLER = "disableController",
  // permit2 specific
  PERMIT = "permit",
  PERMIT_BATCH = "permitBatch",
  // reward streams specific
  ENABLE_REWARD = "enableReward",
  DISABLE_REWARD = "disableReward",
  CLAIM_REWARDS = "claimRewards",
  CLAIM_REWARD = "claimReward",
  UNLOCK_REUL = "unlockREul",
  CLAIM_MERKL_REWARD = "claimMerklReward",
  // swap specific
  SWAP = "swap",
  SWAP_MULTICALL = "swap_multicall",
  SWAP_REPAY_AND_DEPOSIT = "swap_repayAndDeposit",
  SWAP_DEPOSIT = "swap_deposit",
  SWAP_SWEEP = "swap_sweep",
  SWAP_BORROW_AND_REPAY = "swap_borrowAndRepay",
  // swap verifier specific
  SWAP_VERIFIER_SKIM_MIN = "swapVerifierSkimMin",
  SWAP_VERIFIER_DEBT_MAX = "swapVerifierDebtMax",
  TOU = "tou",
  // fee flow specific
  FEE_FLOW_BUY = "feeFlowBuy",
  // native wrapping/unwrapping
  WRAP = "wrap",
  UNWRAP = "unwrap",
  // keyring verification
  KEYRING_VERIFY = "keyringVerify",
  // euler swap
  UNINSTALL = "uninstall",
  EULER_SWAP_DEPLOY_POOL = "eulerSwapDeployPool",
  SET_ACCOUNT_OPERATOR = "setAccountOperator",
}

export const NATIVE_ADDRESS =
  "0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee" as const

export type BaseAsset = {
  address: Address
  name: string
  symbol: string
  decimals: number
  logoURI?: string
}

export type NativeAsset = BaseAsset & {
  address: typeof NATIVE_ADDRESS
}

type NonNative<T> = T extends { address: typeof NATIVE_ADDRESS } ? never : T

export type Asset = NonNative<BaseAsset>

export function isNativeAsset(asset: Asset): asset is NativeAsset {
  return asset.address === NATIVE_ADDRESS
}

export type TransactionItemBase<
  item extends
    | VaultTransaction
    | SwapTransaction
    | PermitTransactionBase
    | RewardStreamsTransaction
    | MerklTransaction
    | UnlockREulTransaction
    | SwapVerifierTransaction
    | TosTransaction
    | KeyringTransaction
    | FeeFlowTransaction
    | EulerSwapTransaction
    | UninstallTransaction
    | WETHTransaction,
  asset extends Asset | NativeAsset = Asset | NativeAsset,
> = {
  asset: asset
  from: Address
  isMax?: boolean
  multiStepIndex?: number
  result?: BatchResultStatus<ContractFunctionResult>
  to: Address
  value: string
  vaultType?: GenericVaultType
  subLabel?: string
} & item

export type TransactionItem =
  | VaultTransactionItem
  | SwapTransactionItem
  | RewardStreamsTransactionItem
  | MerklTransactionItem
  | UnlockREulTransactionItem
  | EvcTransaction
  | PermitTransactionItem
  | SwapVerifierTransaction
  | TosTransactionItem
  | KeyringTransactionItem
  | FeeFlowTransactionItem
  | SkimTransactionItem
  | SkimInTransactionItem
  | WETHTransactionItem
  | EulerSwapTransactionItem
  | UninstallTransactionItem

export type TransactionItemUnion =
  | TransactionItem
  | CompoundTransactionItem
  | CompoundTransactionUnion

export type TransactionItems = TransactionItemUnion[]

export enum TransactionSubLabel {
  REPAY_FROM_WALLET = "repayFromWallet",
  REPAY_WITH_SHARES_SAME_ASSET_AND_VAULT = "repayWithSharesSameAssetsAndVaults",
  REPAY_WITH_SHARES_SAME_ASSET_AND_DIFFERENT_VAULT = "repayWithSharesSameAssetsAndDifferentVaults",
  REPAY_WITH_SHARES_SWAP_EXACT_OUT = "repayWithSharesSwapExactOut",
  REPAY_WITH_SHARES_SWAP_EXACT_IN = "repayWithSharesSwapExactIn",
}

export type ManagedVaultTransactionItem = TransactionItemUnion & {
  vaultType: GenericVaultType.Managed
}

export function isManagedVaultTransactionItem(
  item?: TransactionItemUnion,
): item is ManagedVaultTransactionItem {
  return !!(
    item &&
    "vaultType" in item &&
    item?.vaultType === GenericVaultType.Managed
  )
}
